import React, { useState, useRef, useEffect } from 'react';
import { User } from '../../services/authService';
import { FinanceService, FinanceLesson, FinanceConcept, LessonSummary } from '../../services/financeService';
import PageHeader from '../common/PageHeader';
import GenericHistorySelector from '../shared/GenericHistorySelector';
import GenericPastLessons from '../shared/GenericPastLessons';
import './FinanceForInvestor.css';

interface FinanceForInvestorProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type TabType = 'text' | 'concepts';
type ViewMode = 'current' | 'past-list' | 'past-lesson';

const FinanceForInvestor: React.FC<FinanceForInvestorProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const [lesson, setLesson] = useState<FinanceLesson | null>(null);
  const [expandedConcepts, setExpandedConcepts] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('text');
  const [showMenu, setShowMenu] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [selectedPastLesson, setSelectedPastLesson] = useState<FinanceLesson | null>(null);
  const [showHistorySelector, setShowHistorySelector] = useState(false);
  const [pastLessonsList, setPastLessonsList] = useState<LessonSummary[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load latest lesson from database on mount
  useEffect(() => {
    loadLatestLesson();
  }, []);

  const loadLatestLesson = async () => {
    try {
      const result = await FinanceService.getPastLessons(1);
      if (result.lessons.length > 0) {
        const latestLesson = await FinanceService.getLessonById(result.lessons[0].lesson_id);
        setLesson(latestLesson);
      }
    } catch (err) {
      console.error('Error loading latest lesson:', err);
      // It's okay if there are no lessons yet
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleOpenGenerateLesson = async () => {
    try {
      setShowMenu(false);
      // Fetch past lessons for the selector
      const result = await FinanceService.getPastLessons(100);
      setPastLessonsList(result.lessons);
      setShowHistorySelector(true);
    } catch (err) {
      console.error('Error loading past lessons:', err);
      // Open selector anyway, it will show empty state
      setPastLessonsList([]);
      setShowHistorySelector(true);
    }
  };

  const handleGenerateLesson = async (selectedIds: string[], customInstruction: string) => {
    try {
      setShowHistorySelector(false);
      setLoading(true);
      setError(null);
      setExpandedConcepts(new Set());
      
      const newLesson = await FinanceService.generateLesson(
        selectedIds,
        customInstruction || undefined
      );
      
      setLesson(newLesson);
      setActiveTab('text');
      setViewMode('current');
    } catch (err) {
      console.error('Error generating lesson:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate lesson. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPastLessons = () => {
    setShowMenu(false);
    setViewMode('past-list');
  };

  const handleSelectPastLesson = (pastLesson: FinanceLesson) => {
    setSelectedPastLesson(pastLesson);
    setViewMode('past-lesson');
    setActiveTab('text');
  };

  const handleBackToCurrentLesson = () => {
    setViewMode('current');
    setSelectedPastLesson(null);
  };

  const toggleConcept = (index: number) => {
    const newExpanded = new Set(expandedConcepts);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedConcepts(newExpanded);
  };

  // Get the current lesson being displayed
  const getCurrentLesson = () => {
    if (viewMode === 'past-lesson' && selectedPastLesson) {
      return selectedPastLesson;
    }
    return lesson;
  };

  // Render lesson text (plain text)
  const renderLessonText = () => {
    const currentLesson = getCurrentLesson();
    if (!currentLesson) return null;

    return (
      <div className="finance-text-content">
        <p className="finance-text-plain">{currentLesson.text}</p>
      </div>
    );
  };

  // Determine current lesson and view state
  const currentLesson = getCurrentLesson();
  const showBackButton = viewMode === 'past-lesson';

  return (
    <div className="finance-learn-container">
      {/* If in past lessons list view, show GenericPastLessons component */}
      {viewMode === 'past-list' && (
        <GenericPastLessons
          service={FinanceService}
          onSelectLesson={handleSelectPastLesson}
          onClose={handleBackToCurrentLesson}
        />
      )}

      {/* Main lesson view */}
      {viewMode !== 'past-list' && (
        <>
      {/* Page Header */}
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          showBackButton ? (
            <button 
              className="page-header-icon-btn finance-back-btn"
              onClick={handleBackToCurrentLesson}
              aria-label="Back to current lesson"
            >
              ←
            </button>
          ) : undefined
        }
        centerContent={
          <h1 className="page-header-center-title">
            {viewMode === 'past-lesson' ? 'Past Lesson' : 'Finance for Investors'}
          </h1>
        }
        rightActions={
          <div className="finance-menu-container" ref={menuRef}>
            <button 
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
              disabled={loading}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="finance-menu-dropdown">
                <button
                  className="finance-menu-item"
                  onClick={handleOpenGenerateLesson}
                >
                  ✨ Generate New Lesson
                </button>
                <button
                  className="finance-menu-item"
                  onClick={handleOpenPastLessons}
                >
                  📚 Past Lessons
                </button>
              </div>
            )}
          </div>
        }
      />

      {/* Main Content */}
      <div className="finance-main-content">
        {/* Loading State */}
        {loading && (
          <div className="finance-loading">
            <div className="finance-spinner-large"></div>
            <p className="finance-loading-text">Generating lesson...</p>
          </div>
        )}

        {/* Error Message */}
        {error && !loading && (
          <div className="finance-error">
            <p>{error}</p>
          </div>
        )}

        {/* Lesson Content - Text Tab */}
        {currentLesson && !loading && activeTab === 'text' && (
          <div className="finance-chapter">
            <h1 className="finance-lesson-title">{currentLesson.title}</h1>
            <div className="finance-lesson-meta">
              <span className="finance-lesson-level">Level: {currentLesson.level}</span>
            </div>
            <div className="finance-lesson-objective">
              {currentLesson.lesson_objective}
            </div>
            {renderLessonText()}
          </div>
        )}

        {/* Concepts Tab */}
        {currentLesson && !loading && activeTab === 'concepts' && (
          <div className="finance-concepts-section">
            <h2 className="finance-concepts-section-header">Key Concepts ({currentLesson.concepts?.length || 0})</h2>
            {currentLesson.concepts && currentLesson.concepts.length > 0 ? (
              <div className="finance-concepts-list">
                {currentLesson.concepts.map((concept, index) => (
                  <div key={index} className="finance-concept-card">
                    <button
                      className="finance-concept-header"
                      onClick={() => toggleConcept(index)}
                    >
                      <div className="finance-concept-header-content">
                        <span className="finance-concept-title">{concept.term}</span>
                        <span className="finance-concept-category">{concept.category}</span>
                      </div>
                      <span className="finance-concept-arrow">
                        {expandedConcepts.has(index) ? '▼' : '▶'}
                      </span>
                    </button>
                    {expandedConcepts.has(index) && (
                      <div className="finance-concept-content">
                        <div className="finance-concept-definition">
                          <strong>Definition:</strong>
                          <p>{concept.definition}</p>
                        </div>
                        {concept.example && (
                          <div className="finance-concept-example">
                            <strong>Example:</strong>
                            <p>{concept.example}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="finance-no-concepts">No concepts available for this lesson.</p>
            )}
          </div>
        )}

        {/* Welcome Message */}
        {!currentLesson && !loading && !error && (
          <div className="finance-welcome">
            <h2>Welcome to Finance for Investors</h2>
            <p>Click the menu (⋮) in the top right and select "Generate New Lesson" to create an AI-powered investment lesson.</p>
            <div className="finance-features">
              <div className="finance-feature">
                <span className="finance-feature-icon">📊</span>
                <span>Investment Fundamentals</span>
              </div>
              <div className="finance-feature">
                <span className="finance-feature-icon">💼</span>
                <span>Portfolio Strategies</span>
              </div>
              <div className="finance-feature">
                <span className="finance-feature-icon">📈</span>
                <span>Market Analysis</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Tab Bar */}
      {currentLesson && !loading && (
        <div className="finance-bottom-bar">
          <button
            className={`finance-tab-button ${activeTab === 'text' ? 'active' : ''}`}
            onClick={() => setActiveTab('text')}
          >
            Text
          </button>
          <button
            className={`finance-tab-button ${activeTab === 'concepts' ? 'active' : ''}`}
            onClick={() => setActiveTab('concepts')}
          >
            Concepts ({currentLesson.concepts?.length || 0})
          </button>
        </div>
      )}

      {/* History Selector Modal */}
      <GenericHistorySelector
        isOpen={showHistorySelector}
        pastLessons={pastLessonsList}
        onGenerate={handleGenerateLesson}
        onClose={() => setShowHistorySelector(false)}
        customInstructionPlaceholder="e.g., Understanding P/E ratios, Portfolio diversification strategies..."
      />
        </>
      )}
    </div>
  );
};

export default FinanceForInvestor;

